import { AlertCircle, AlertTriangle, Info, type LucideIcon } from 'lucide-react';
import { cn } from '@/utils/cn';
import type { AlarmSeverity, SeverityPillVariant } from './types';

interface Props {
  severity: AlarmSeverity;
  variant?: SeverityPillVariant;
  className?: string;
}

interface SeverityConfig {
  icon: LucideIcon;
  bg: string;
  bgSolid: string;
  text: string;
  border: string;
  label: string;
}

const SEVERITY_CONFIG: Record<AlarmSeverity, SeverityConfig> = {
  critical: {
    icon: AlertCircle,
    bg: 'bg-ems-red/15',
    bgSolid: 'bg-ems-red',
    text: 'text-ems-red',
    border: 'border-ems-red/30',
    label: 'Critical',
  },
  warning: {
    icon: AlertTriangle,
    bg: 'bg-ems-amber/15',
    bgSolid: 'bg-ems-amber',
    text: 'text-ems-amber',
    border: 'border-ems-amber/30',
    label: 'Warning',
  },
  info: {
    icon: Info,
    bg: 'bg-ems-blue/15',
    bgSolid: 'bg-ems-blue',
    text: 'text-ems-blue',
    border: 'border-ems-blue/30',
    label: 'Info',
  },
};

export function SeverityPill({ severity, variant = 'pill', className }: Props) {
  const cfg = SEVERITY_CONFIG[severity];
  const Icon = cfg.icon;

  if (variant === 'dot') {
    return (
      <span
        role="img"
        aria-label={cfg.label}
        data-severity={severity}
        className={cn('inline-block w-2 h-2 rounded-full', cfg.bgSolid, className)}
      />
    );
  }

  if (variant === 'icon') {
    return (
      <Icon
        aria-label={cfg.label}
        data-severity={severity}
        className={cn('w-4 h-4', cfg.text, className)}
      />
    );
  }

  return (
    <span
      data-severity={severity}
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold border',
        cfg.bg,
        cfg.text,
        cfg.border,
        className,
      )}
    >
      <Icon className="w-3 h-3" aria-hidden="true" />
      {cfg.label}
    </span>
  );
}
